import React from 'react';
import { Box, Flex, Text, Button } from '@indeed/ifl-components';
import type { SystemStyleObject } from '@indeed/ifl-css';
import { Attachment } from '@indeed/ifl-icons';
import { SafeMarkup } from '@indeed/jspl-foundations';
import useGettext from '../../util/GettextFactory';

/** Answer types this component knows how to format; other/future values render as plain text. */
type KnownAnswerType =
    | 'SELECT'
    | 'MULTISELECT'
    | 'HIERARCHICAL'
    | 'DATE'
    | 'FILE'
    | 'TEXT'
    | 'TEXTAREA';

export type AnswerPreviewType = KnownAnswerType | (string & {});

export interface AnswerPreviewProps {
    /** Question type — drives per-type formatting. */
    type: AnswerPreviewType;
    /** Answer value(s). For SELECT/MULTISELECT these are option values resolved to labels via `options`. */
    values: string[];
    /** Options for SELECT/MULTISELECT label resolution. */
    options?: { value: string; label: string }[];
    /** Text size level (default 2). */
    level?: 1 | 2 | 3;
    /** Locale for formatting DATE answers. When omitted, the raw value is shown. */
    locale?: string;
    /** Style override merged into the answer text. */
    sx?: SystemStyleObject;
}

/** Threshold (chars) above which text answers get expand/collapse treatment */
const LONG_TEXT_THRESHOLD = 100;

/**
 * Formats an ISO (YYYY-MM-DD) date answer for the given locale. Anything else — no locale, a
 * non-ISO/already-formatted string, or an unparseable value — is passed through untouched.
 * Parsing/formatting in UTC keeps the date-only value from shifting a day across time zones.
 */
const formatDateValue = (value: string, locale?: string): string => {
    if (!locale || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    }).format(date);
};

/**
 * Renders a single answer value with type-appropriate formatting.
 * Returns null when there is no answer value.
 */
export const AnswerPreview: React.FunctionComponent<AnswerPreviewProps> = ({
    type,
    values,
    options = [],
    level = 2,
    locale,
    sx,
}) => {
    const [expanded, setExpanded] = React.useState(false);
    const gt = useGettext();
    const textId = React.useId();

    if (values.length === 0) return null;

    // Answers render in the strong foreground color per design; also pins the color so
    // jspl-foundations 9's SafeMarkup (which defaults to `color: inherit`) doesn't drift.
    const answerSx: SystemStyleObject = { fontWeight: '700', color: 'foreground.strong', ...sx };

    switch (type) {
        case 'SELECT': {
            const rawValue = values[0];
            const resolved = options.find((o) => o.value === rawValue)?.label ?? rawValue;
            return (
                <SafeMarkup inline level={level} sx={answerSx}>
                    {resolved}
                </SafeMarkup>
            );
        }

        case 'MULTISELECT': {
            if (values.length === 1) {
                const resolved = options.find((o) => o.value === values[0])?.label ?? values[0];
                return (
                    <SafeMarkup inline level={level} sx={answerSx}>
                        {resolved}
                    </SafeMarkup>
                );
            }
            return (
                <Box
                    as="ul"
                    sx={{
                        margin: 0,
                        paddingInlineStart: 5,
                        fontWeight: '700',
                    }}
                >
                    {values.map((v, index) => {
                        const resolved = options.find((o) => o.value === v)?.label ?? v;
                        return (
                            <li key={`${v}-${index}`}>
                                <SafeMarkup inline level={level} sx={answerSx}>
                                    {resolved}
                                </SafeMarkup>
                            </li>
                        );
                    })}
                </Box>
            );
        }

        case 'HIERARCHICAL':
            return (
                <Text level={level} sx={answerSx}>
                    {values.join(' › ')}
                </Text>
            );

        case 'DATE':
            return (
                <Text level={level} sx={answerSx}>
                    {formatDateValue(values[0], locale)}
                </Text>
            );

        case 'FILE':
            return (
                <Flex sx={{ alignItems: 'center', gap: 2 }}>
                    <Attachment aria-hidden />
                    <Text level={level} sx={answerSx}>
                        {values[0]}
                    </Text>
                </Flex>
            );

        case 'TEXTAREA':
        case 'TEXT':
        default: {
            const text = values.join(', ');
            if (text.length <= LONG_TEXT_THRESHOLD) {
                return (
                    <Text level={level} sx={answerSx}>
                        {text}
                    </Text>
                );
            }
            return (
                <Box>
                    <Text
                        id={textId}
                        level={level}
                        sx={{
                            ...answerSx,
                            marginBlockEnd: 0,
                            ...(!expanded && {
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }),
                        }}
                    >
                        {text}
                    </Text>
                    <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        aria-expanded={expanded}
                        aria-controls={textId}
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            display: 'block',
                            paddingInline: 0,
                            paddingBlock: 0,
                            minInlineSize: 'auto',
                            minBlockSize: 'auto',
                            fontSize: 2,
                            lineHeight: 'body.2.lineHeight',
                        }}
                    >
                        {expanded ? gt.gettext('Show less') : gt.gettext('Show more')}
                    </Button>
                </Box>
            );
        }
    }
};
